import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { QUERY_KEYS } from '../../../lib/queryKeys'
import { createPickupOrder, deletePickupOrder, getPickupOrders, updatePickupOrder, uploadPickupOrderBarcode } from '../pickupOrdersService'
import { PickupOrder, PickupOrderCancelReason, PickupOrderStatus } from '../types/PickupOrder'

export const usePickupOrders = (status?: PickupOrderStatus | 'all', enabled = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.PICKUP_ORDERS],
    queryFn: () => getPickupOrders(),
    select: data => {
      if (status === 'all') return data
      return data.filter(order => order.status === status)
    },
    enabled,
  })
}

export const useCreatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createPickupOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS], exact: false })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
  })
}

export const useUpdatePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      status,
      pkgCount,
      cancelReason,
    }: {
      id: string
      status: PickupOrderStatus
      pkgCount?: number
      cancelReason?: PickupOrderCancelReason
    }) => updatePickupOrder(id, { status, pkgCount, cancelReason }),
    onMutate: async ({ id, status, pkgCount, cancelReason }) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS] })
      const previousOrders = queryClient.getQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS])
      queryClient.setQueryData<PickupOrder[]>([QUERY_KEYS.PICKUP_ORDERS], old =>
        old?.map(order =>
          order.id === id
            ? {
                ...order,
                status,
                ...(pkgCount !== undefined && { pkgCount }),
                ...(cancelReason !== undefined && { cancelReason }),
              }
            : order
        )
      )
      return { previousOrders }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
    onError: (_error, _variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData([QUERY_KEYS.PICKUP_ORDERS], context.previousOrders)
      }
    },
  })
}

export const useUploadPickupOrderBarcode = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, barcodeFile }: { id: string; barcodeFile: File }) => uploadPickupOrderBarcode(id, barcodeFile),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS], exact: false })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
  })
}

export const useDeletePickupOrder = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deletePickupOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PICKUP_ORDERS], exact: false })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PICKUP_ORDERS] })
    },
  })
}
